import { Component, OnInit, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  Subject,
  takeUntil,
  take,
} from 'rxjs';
import { includesIgnoreCase } from 'src/utils';
import { showLoading } from 'src/app/shared/components/modals/loading';
import { map, switchMap } from 'rxjs/operators';
import { JobsStore } from './jobs-store';
import { FormService } from 'src/app/shared/components/form/form.service';
import { JobsService } from './jobs.service';
import { IJobsFilter, jobDiscordModalData, jobPremiumModalData } from './jobs';
import { UserService } from 'src/app/auth/user.service';
import { JobsServicesService } from '../jobs-services.service';
import { CrudHelper } from 'src/app/firebase/helpers/crudHelper';
import { AuthService } from '../../../firebase/auth.service';
import { environment } from '../../../../environments/environment';
@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.scss'],
})
export class JobsComponent implements OnInit, OnDestroy {
  searchString: BehaviorSubject<string> = new BehaviorSubject<string>('');

  status;
  loader = showLoading();

  isOpenFormModal = false;
  myContent: boolean = false;
  filter = {};

  userId: string;
  items;

  color = 'dark';
  isAdmin = false;
  hasMembership = false;
  isPremium;

  isOpenDiscordModal = false;
  premiumModalData = jobPremiumModalData;
  serviceDiscordModalData = jobDiscordModalData;
  isUserPremiumModalOpen = false;
  currentUser;
  infoSnapshot;
  userInfo;
  duration;
  currentTime: number = new Date().getTime();

  isMobile: boolean;
  isIosApp: boolean;
  isUserHasSuspendedContent$ = new BehaviorSubject<boolean>(false);
  isPremium$ = new BehaviorSubject<boolean>(false);
  private destroy$ = new Subject<void>();

  constructor(
    private jobsStore: JobsStore,
    private jobsService: JobsService,
    private fs: FormService,
    private userService: UserService,
    private platform: Platform,
    private service: JobsServicesService,
    private crudHelper: CrudHelper,
    private authService: AuthService
  ) {
    this.isMobile = this.platform.platforms().includes('capacitor');
    this.isIosApp = this.platform.platforms().includes('ios') && this.isMobile;
  }

  async ngOnInit() {
    this.getUser();
    this.service.isUserHasSuspendedContent$.subscribe(data =>
      this.isUserHasSuspendedContent$.next(data)
    );
    this.listenFilter();
    this.listenItemsChanges();
    this.listenStatusChanges();
    this.jobsStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });

    this.userService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isPremium = this.userService.isPremium;
        this.isPremium$.next(this.isPremium);
      });

    this.jobsStore.getItems(false);
  }

  ngOnDestroy() {
    this.fs.formInitialValues.next({ myContent: null });
    this.destroy$.next();
    this.destroy$.complete();
    this.fs.jobsMenuData$.next(false);
  }

  openModal() {
    const uid =
      this.currentUser?.uid || this.authService.getCurrentUser()?.uid;
    this.isPremium = !!this.userService.isPremium;
    this.isAdmin = !!this.userService.isAdmin;

    if (!environment.production) {
      console.info('[premium-gate]', {
        gate: 'jobs-service-offer',
        uid,
        isPremium: this.isPremium,
        isPaidPremium: !!this.userService.isPaidPremium,
        isAdmin: this.isAdmin,
        decision:
          uid && (this.isPremium || this.isAdmin)
            ? 'open'
            : 'premium',
      });
    }

    if (!uid && !this.isPremium && !this.isAdmin) {
      this.isUserPremiumModalOpen = true;
      return;
    }

    if (this.isPremium || this.isAdmin) {
      this.jobsStore.updateShowFormModal(true);
    } else {
      this.isUserPremiumModalOpen = true;
    }
  }

  closeModal(e) {
    e ? this.jobsStore.updateShowFormModal(false) : null;
  }

  onSearchChange(searchString) {
    this.searchString.next(searchString);
  }

  private listenFilter() {
    this.fs.jobsMenuData$.subscribe(data => {
      this.filter = data;
      const initialData = this.jobsService.checkFilter(data);
      this.color = initialData ? 'success' : 'dark';
      this.myContent = (data as IJobsFilter).myContent;

      this.jobsStore.getItems(data);
    });
  }

  private listenStatusChanges() {
    this.jobsStore
      .selectLoading()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async loading => {
        loading ? (await this.loader).present() : (await this.loader).dismiss();
      });
  }

  private listenItemsChanges() {
    combineLatest([this.jobsStore.selectItems(), this.searchString])
      .pipe(
        takeUntil(this.destroy$),
        map(([items, searchString]) => {
          if (!items) {
            return [];
          }

          let filteredItems = items;

          // Only apply search filter if there's a search string
          if (searchString) {
            filteredItems = filteredItems.filter(item =>
              includesIgnoreCase(item.title, searchString)
            );
          }

          return filteredItems;
        }),
        switchMap((items: any[]) => {
          if (!items?.length) return (this.items = []);
          const users = this.crudHelper.getCollectionItems({
            collectionName: 'users',
            queryFn: null,
          });
          return users.pipe(
            filter(users => !!users),
            map(users => {
              const itemsWithUserData = items.map(item => {
                const user: any = users.find(
                  (user: any) => user.id === item.creator.id
                );
                if (user) return Object.assign({ ...item, creator: user });
                return item;
              });
              return itemsWithUserData;
            })
          );
        })
      )
      .subscribe(items => {
        if (!this.myContent) {
          this.items = items
            ?.filter(
              item => !this.currentUser?.blockList?.includes(item.creator.id)
            )
            .filter(
              item => item.suspended === false || item?.suspended === undefined
            );
        } else {
          this.items = items?.filter(
            item => !this.currentUser?.blockList?.includes(item.creator.id)
          );
        }
        this.isUserHasSuspendedContent$.next(
          this.jobsService.jobsSuspendedCheck(items, this.currentUser?.uid)
        );
      });
  }
  private async getUser() {
    const user = await this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid),
        take(1)
      )
      .toPromise();

    if (user) {
      this.currentUser = user;
      this.isPremium = this.userService.isPremium;
      this.isPremium$.next(this.isPremium);
      this.isAdmin = this.userService.isAdmin;
      this.userId = user.uid;
      this.userInfo = this.userService.balanceInfo;
      this.hasMembership = await this.authService.getMembership(
        this.currentUser.uid
      );
    }
  }

  jobsSuspendedCheck() {
    return this.jobsService.jobsSuspendedCheck(
      this.items,
      this.currentUser?.uid
    );
  }
}

